import { AHSeparator, useLayoutContext, useTheme } from "arkh";
import React, { useCallback, useMemo } from "react";
import { FlatList } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FileIntegrationListItem, FileListItem } from "./FileListItem";
import { onPressIntegrationFileType } from "./FilesScreenComponent";
import { openFileType } from "./useOpenFile";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { AllFilesComponent_Store$key } from "./__generated__/AllFilesComponent_Store.graphql";
import { AllFilesComponentQuery } from "./__generated__/AllFilesComponentQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";

type Props = {
  onPressFile: openFileType;
  onPressIntegrationFile?: onPressIntegrationFileType;
  includeIntegrationFiles: boolean;
};

export function StoreAllFilesComponent(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => <StoreAllFilesComponentC {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}

function StoreAllFilesComponentC(
  props: Props & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<AllFilesComponentQuery>(
    graphql`
      query AllFilesComponentQuery($first: Int!, $after: String) {
        store {
          ...AllFilesComponent_Store
        }
      }
    `,
    {
      first: 10,
      after: null,
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: props.fetchKey,
    }
  );
  return <InnerAllFilesComponent {...props} store={data.store} />;
}

function InnerAllFilesComponent(
  props: Props & { store: AllFilesComponent_Store$key }
) {
  const layout = useLayoutContext();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { data, loadNext, hasNext } = usePaginationFragment(
    graphql`
      fragment AllFilesComponent_Store on Store
      @refetchable(queryName: "AllFilesComponentPaginationQuery") {
        id
        integrationFlyers {
          id
          eMaginId
          title
        }
        files(first: $first, after: $after, orderBy: NAME)
          @connection(key: "AllFilesComponent_store_files") {
          edges {
            node {
              id
              name

              ...FileListItem_file
            }
          }
        }
      }
    `,
    props.store
  );

  const flyers = useMemo(
    () =>
      !props.includeIntegrationFiles
        ? []
        : (data.integrationFlyers
            ?.filter((flyer) => flyer.eMaginId)
            .map((flyer) => ({
              type: "integration" as const,
              file: flyer,
            })) ?? []),
    [data.integrationFlyers, props.includeIntegrationFiles]
  );

  const listData = useMemo(() => {
    return [
      ...flyers,
      ...(data.files.edges
        .filter((edge) => edge.node)
        .map((edge) => ({
          file: edge.node,
          type: "file" as const,
        })) ?? []),
    ];
  }, [data.files.edges, flyers]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      if (item.type === "integration") {
        if (!item.file.eMaginId) {
          return null;
        }
        return (
          <FileIntegrationListItem
            isFirst={index === 0}
            isLast={index === listData.length - 1}
            onPress={() => {
              if (!item.file.eMaginId) {
                return null;
              }
              props.onPressIntegrationFile?.({
                id: item.file.eMaginId,
                title: item.file.title ?? "",
              });
            }}
            title={item.file.title ?? ""}
          />
        );
      }

      return (
        <FileListItem
          isFirst={index === 0}
          isLast={index === listData.length - 1}
          file={item.file}
          nameHighlights={[
            { text: item.file.name ?? "", isHighlighted: false },
          ]}
          textHighlights={[]}
          onPress={props.onPressFile}
        />
      );
    },
    [listData.length, props]
  );

  return (
    <FlatList
      onEndReached={() => {
        if (hasNext) {
          loadNext(10);
        }
      }}
      keyExtractor={(item) => item.file.id}
      onEndReachedThreshold={0.5}
      keyboardShouldPersistTaps="always"
      ItemSeparatorComponent={AHSeparator}
      contentContainerStyle={{
        paddingBottom: insets.bottom + layout.space.large,
        paddingHorizontal: layout.space.medium,
        paddingTop: layout.space.medium,
      }}
      style={{ flex: 1, backgroundColor: theme.background }}
      scrollIndicatorInsets={{ bottom: insets.bottom + layout.space.large }}
      data={listData}
      renderItem={renderItem}
    />
  );
}
